import { Component, ElementRef, HostListener, inject, Input, OnChanges, OnDestroy, output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';

import { debounceTime, distinctUntilChanged, fromEvent, Subscription, tap } from 'rxjs';

import { MarkAllTouchedDirective } from '@bn/web-ui';
import { AddonItem } from '@bn/web-ui/addons/models';
import { InputAction } from '@bn/web-ui/input/enums';
import { InputComponent } from '@bn/web-ui/input/input.component';

@Component({
  selector: 'bn-layout-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss'],
  imports: [ReactiveFormsModule, InputComponent, MarkAllTouchedDirective],
})
export class SearchComponent implements OnChanges, OnDestroy {
  @Input() public placeholder = 'Search';
  @Input() public isLoading = false;
  @Input() public isOpen = false;
  @Input() public minSearchTermLength = 3;
  @Input() public debounceMs = 500;

  public readonly searchTermChange = output<string>();
  public readonly inputClick = output<void>();
  public readonly clear = output<void>();
  public readonly clickOutside = output<void>();
  public readonly arrowUp = output<void>();
  public readonly arrowDown = output<void>();
  public readonly enter = output<void>();
  public readonly escape = output<void>();

  public readonly form = new FormGroup({
    searchTerm: new FormControl<string>('', { nonNullable: true }),
  });

  public readonly leftInputAddons: AddonItem[] = [{ icon: 'magnifying-glass', size: 'xs' }];
  public rightInputAddons: AddonItem[] = [];

  private readonly clearAddon: AddonItem = {
    icon: 'close',
    action: InputAction.Clear,
    tooltip: 'Clear',
    size: 'xs',
  };

  private readonly elementRef = inject(ElementRef);
  private readonly subscriptions = new Subscription();
  private clickOutsideSubscription = new Subscription();

  public constructor() {
    this.subscriptions.add(
      this.form.controls.searchTerm.valueChanges
        .pipe(
          tap((term) => (this.rightInputAddons = term.length > 0 ? [this.clearAddon] : [])),
          debounceTime(this.debounceMs),
          distinctUntilChanged(),
        )
        .subscribe((term) => {
          if (term.length < this.minSearchTermLength) return;
          this.searchTermChange.emit(term);
        }),
    );
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']) this.toggleClickOutsideListener(changes['isOpen'].currentValue);
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.clickOutsideSubscription.unsubscribe();
  }

  @HostListener('document:keyup', ['$event'])
  public onKeyUp(event: KeyboardEvent): void {
    if (!event.key) return;

    switch (true) {
      case event.key.endsWith('Enter'):
        this.enter.emit();
        break;
      case event.key.endsWith('Escape'):
        this.escape.emit();
        break;
      case event.key.endsWith('ArrowUp'):
        this.arrowUp.emit();
        break;
      case event.key.endsWith('ArrowDown'):
        this.arrowDown.emit();
        break;
    }
  }

  public onInputClick(): void {
    this.inputClick.emit();
  }

  public onAddonClick(addon: AddonItem): void {
    if (addon.action !== InputAction.Clear) return;
    this.form.controls.searchTerm.setValue('');
    this.rightInputAddons = [];
    this.clear.emit();
  }

  private toggleClickOutsideListener(isOpen: boolean): void {
    this.clickOutsideSubscription.unsubscribe();
    if (!isOpen) return;

    this.clickOutsideSubscription = fromEvent<MouseEvent>(document, 'click').subscribe((event) => {
      if (!this.elementRef.nativeElement.contains(event.target)) if (!this.elementRef.nativeElement.contains(event.target)) this.clickOutside.emit();
    });
  }
}
